import React, { useEffect, useState } from 'react';
import { ArrowRight, Bot, Shield, ListChecks, BookMarked, History, Workflow } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchMcpConfigDashboardData, type McpConfigDashboardData } from '../../lib/mcpConfigDashboard';

function loadErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return 'Failed to load MCP configuration';
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

const McpConfigDashboardPanel: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<McpConfigDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchMcpConfigDashboardData()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(loadErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-center justify-between gap-2 mb-3 px-1">
        <div className="flex items-center gap-2 min-w-0">
          <Bot className="w-5 h-5 text-violet-500 shrink-0" />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">MCP configuration</h2>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
              Agent memory for ChatGPT connector — Tier 1–4
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/agent-directives')}
          className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-400 hover:underline shrink-0"
        >
          Open
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading MCP config…</div>
      ) : error ? (
        <div className="py-6 px-3 text-center text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
          {error}
        </div>
      ) : data ? (
        <button
          type="button"
          onClick={() => navigate('/agent-directives')}
          className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 hover:border-violet-300 dark:hover:border-violet-700 transition-colors"
        >
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-sky-500 mt-0.5 shrink-0" />
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-white leading-none">Deploy</div>
                <div className="text-[10px] text-gray-500 mt-1">Tier 1 compiled</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ListChecks className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-white leading-none tabular-nums">
                  {data.activeDirectives}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">Tier 2 directives</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <BookMarked className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-white leading-none tabular-nums">
                  {data.activeBriefs}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">Tier 3 briefs</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Workflow className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" />
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-white leading-none tabular-nums">
                  {data.activeWorkflows}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">Tier 4 workflows</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <History className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-white leading-none tabular-nums">
                  {data.revisionsLast7Days}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">Changes (7d)</div>
              </div>
            </div>
          </div>

          {data.recentDirectives.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {data.recentDirectives.map((d) => (
                <div key={`${d.key}-${d.scope}`} className="text-[11px] text-gray-600 dark:text-gray-400">
                  <span className="font-mono text-violet-600 dark:text-violet-400">{d.key}</span>
                  <span className="text-gray-400 mx-1">·</span>
                  <span className="line-clamp-1">{d.directive}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200/80 dark:border-gray-700/80">
            <span>Last updated {formatWhen(data.lastUpdatedAt)}</span>
            <span className="font-mono truncate max-w-full">{data.hostedMcpUrl}</span>
          </div>
        </button>
      ) : null}
    </div>
  );
};

export default McpConfigDashboardPanel;
